import React, { useEffect, useState } from 'react';
import {
  Box,
  BoxProps,
  Button,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
  Container,
} from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../../hooks';
import {
  getSubTopicState,
  IGroupedSubTopic,
} from '../../../../store/SubTopicSlice';
import { getSubTopicUrl } from '..';
import SubTopicListModal from '../modals/SubTopicListModal';
import SubTopicProgress from './SubTopicProgress';
import WelcomePopup from './WelcomePopup';
import { ITopic } from '../../../../store/TopicSlice';
import {
  getProgressState,
  getSubjectProgress,
  saveStudentProgress,
} from '../../../../store/ProgressSlice';
import { getAuthState } from '../../../../store/AuthSlice';

const boxProps: BoxProps = {
  component: 'section',
  sx: {
    top: '120px',
    height: { xs: 'auto', sm: 'auto', md: '65px' },
    display: 'flex',
    alignItems: 'center',
    position: 'fixed',
    width: '100%',
    backgroundColor: '#fff',
    border: '1px solid #E2E8F0',
    flexDirection: { xs: 'column', sm: 'row' },
    justifyContent: { xs: 'space-evenly', sm: 'center' },
    zIndex: 5,
    pt: { xs: 1.5, md: 0 },
    pb: { xs: 1.5, md: 0 },
  },
};

const arrowButtonStyle = {
  minWidth: 'unset',
  p: 0.5,
  backgroundColor: '#E2E8F0',
  color: '#1E293B',
  borderRadius: '50%',
  '&:hover': {
    backgroundColor: '#cdd7e5',
  },
  width: '38px',
  height: '38px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

interface SubTopicPageTopBarProps {
  selectedSubTopicIndex: number;
  subTopics: Array<IGroupedSubTopic>;
  activeSection: string;
  onSectionChange: (section: string) => void;
  topicData: ITopic;
}

const SubTopicPageTopBar: React.FC<SubTopicPageTopBarProps> = ({
  selectedSubTopicIndex,
  subTopics,
  activeSection,
  onSectionChange,
  topicData,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const params = useParams();
  const belowMd = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);

  // Store
  const { subTopic } = useAppSelector(getSubTopicState);
  const progressState = useAppSelector(getProgressState);
  const authState = useAppSelector(getAuthState);
  const authUser = authState.user;

  useEffect(() => {
    if (topicData.curriculum && topicData.subject) {
      dispatch(
        getSubjectProgress({
          curriculum: topicData.curriculum,
          subject: topicData.subject,
          subjectLevel: topicData.subjectLevel,
        })
      );
    }
  }, [topicData.subject]);

  // Show welcome popup when subtopic changes
  useEffect(() => {
    if (subTopic?._id) {
      setShowWelcomePopup(true);
      const timer = setTimeout(() => {
        setShowWelcomePopup(false);
      }, 3000);
      return () => {
        clearTimeout(timer);
        return undefined;
      };
    }
    return undefined;
  }, [subTopic?._id]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getQuizUrl = () => {
    if (subTopic?.topic) {
      return `${getSubTopicUrl(subTopic, subTopic.topic)}/quiz`;
    }
    return '';
  };

  const getNextSubTopicUrl = () => {
    if (
      subTopic?.topic &&
      subTopics.length &&
      selectedSubTopicIndex < subTopics.length - 1
    ) {
      return getSubTopicUrl(
        subTopics[selectedSubTopicIndex + 1],
        subTopic.topic
      );
    }
    return '';
  };

  const getPreviousSubTopicUrl = () => {
    if (subTopic?.topic && subTopics.length && selectedSubTopicIndex > 0) {
      return getSubTopicUrl(
        subTopics[selectedSubTopicIndex - 1],
        subTopic.topic
      );
    }
    return '';
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  // Add function to get current subtopic progress
  const getCurrentSubTopicProgress = () => {
    if (!progressState.subjectProgress || !subTopic?._id) return null;

    const foundSubTopic = progressState.subjectProgress.topics
      .flatMap((topic) => topic.subTopics)
      .find((st) => st._id === subTopic._id);

    return foundSubTopic || null;
  };

  const currentProgress = getCurrentSubTopicProgress();

  const navItems = [
    {
      id: 'resources',
      label: 'Resources',
      icon: '📖',
      show: (subTopic?.resources?.length ?? 0) > 0,
      isComplete: currentProgress?.resourcePart === 34,
    },
    {
      id: 'videos-library',
      label: 'Videos',
      icon: '📹',
      show: (subTopic?.videoLinks?.length ?? 0) > 0,
      isComplete: currentProgress?.videoPart === true,
    },
    {
      id: 'practice-questions',
      label: 'Practice Questions',
      icon: '🏀',
      show: (subTopic?.resources?.length ?? 0) > 0,
      isComplete: currentProgress?.practicePart === 33,
    },
    {
      id: 'quiz',
      label: 'Quiz',
      icon: '🧩',
      show: (subTopic?.quizCount ?? 0) > 0,
      isComplete: currentProgress?.quizPart === 33,
      isQuiz: true,
    },
  ];

  return subTopic ? (
    <Box {...boxProps}>
      <Container maxWidth="lg">
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 0 }}
          alignItems="center"
          justifyContent="space-between"
          sx={{ maxWidth: 'lg', flexWrap: 'wrap' }}
        >
          <Box
            sx={{
              width: { xs: '100%', md: 'auto' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              component="button"
              onClick={handleClick}
              sx={{
                fontSize: { xs: '16px', sm: '16px' },
                fontWeight: 500,
                fontFamily: 'inherit',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                color: 'inherit',
                display: 'flex',
                alignItems: 'center',
                '&:hover': {
                  color: '#1E293B',
                },
              }}
            >
              {truncateText(subTopic.name, 20)}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, ml: '20px', mr: '20px' }}>
              <Button
                component={Link}
                to={getPreviousSubTopicUrl()}
                disabled={selectedSubTopicIndex <= 0}
                sx={arrowButtonStyle}
              >
                <ArrowBackIcon sx={{ fontSize: 17 }} />
              </Button>
              <Button
                component={Link}
                to={getNextSubTopicUrl()}
                disabled={selectedSubTopicIndex >= subTopics.length - 1}
                sx={arrowButtonStyle}
              >
                <ArrowForwardIcon sx={{ fontSize: 17 }} />
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              width: { xs: '100%', md: 'auto' },
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: { xs: 'center', md: 'flex-start' },
              gap: 1,
              mt: { xs: 1, md: 0 },
            }}
          >
            {navItems
              .filter((item) => item.show)
              .map((item) => (
                <Box
                  key={item.id}
                  component={item.isQuiz ? Link : 'button'}
                  to={item.isQuiz ? getQuizUrl() : undefined}
                  onClick={() => {
                    if (!item.isQuiz) {
                      onSectionChange(item.id);
                      // Save progress when navigating to videos-library
                      if (
                        item.id === 'videos-library' &&
                        authUser &&
                        (authUser.role.name === 'Student' ||
                          authUser.role.name === 'Tutor') &&
                        params.subtopicId
                      ) {
                        dispatch(
                          saveStudentProgress({
                            subTopicId: params.subtopicId,
                            videoCompletion: true,
                          })
                        );
                      }
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: activeSection === item.id ? '#1E293B' : '#647488',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    position: 'relative',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    '&::after':
                      activeSection === item.id
                        ? {
                            content: '""',
                            position: 'absolute',
                            bottom: '-1px',
                            left: 0,
                            width: '100%',
                            height: '2px',
                            backgroundColor: '#1E293B',
                          }
                        : {},
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '18px',
                      lineHeight: 1,
                    }}
                  >
                    {item.icon}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '16px',
                      fontWeight: 500,
                      fontFamily: 'inherit',
                    }}
                  >
                    {item.label}
                  </Typography>
                  <CheckCircleIcon
                    sx={{
                      fontSize: 16,
                      ml: 0.25,
                      color: item.isComplete ? '#22C55E' : '#E2E8F0',
                    }}
                  />
                </Box>
              ))}
          </Box>
          <Box
            sx={{
              width: { xs: '100%', md: 'auto' },
              display: 'flex',
              justifyContent: { xs: 'center', md: 'flex-end' },
              mt: { xs: 1, md: 0 },
            }}
          >
            <SubTopicProgress currentProgress={currentProgress} />
            {!belowMd && authState.user && showWelcomePopup && (
              <WelcomePopup
                userName={authState.user.firstname}
                onClose={() => setShowWelcomePopup(false)}
                completionPercentage={currentProgress?.completion ?? 0}
              />
            )}
          </Box>
        </Stack>
      </Container>
      {subTopic.topic && (
        <SubTopicListModal
          open={Boolean(anchorEl)}
          onClose={handleClose}
          subTopics={subTopics}
          selectedSubTopicId={subTopic._id}
          topicData={subTopic.topic}
          anchorEl={anchorEl}
          subTopicsProgress={progressState.subjectProgress?.topics.flatMap(
            (topic) => topic.subTopics
          )}
        />
      )}
    </Box>
  ) : null;
};

export default SubTopicPageTopBar;
